import { useEffect, useMemo, useRef, useState } from "react";
import { normalizeTag, type AppCatalog } from "../services/app-catalog-api";

/**
 * FR-070: the single tag entry control used by both the Question form and the Company form, so
 * the two can't drift apart the way they had (the Company form previously had no catalogue
 * awareness at all — no dropdown, no normalisation, no validation).
 *
 * Three rules, all of them the product owner's (2026-09-03):
 *
 * 1. Clicking the field opens a dropdown of the catalogue right underneath it, filtered as you
 *    type. Free typing still works, for the CO Member who knows the app name and would rather
 *    not reach for the mouse.
 * 2. Tags are stored in the catalogue's canonical casing. "WORKFLOW", "Workflow", "WOrkflow" and
 *    "WorkFLow" are all one tag, `Workflow` — otherwise the same app splits into four spellings
 *    that don't match each other, and a company tagged `WORKFLOW` matches no question tagged
 *    `Workflow`.
 * 3. A tag that isn't in the catalogue is **rejected, not warned about** (this reverses the
 *    2026-09-02 "warn, don't block" decision). It is shown boxed in red so the CO Member can see
 *    exactly which entry was wrong, and it is never part of `value` — so it cannot reach the
 *    server whether they press Save or navigate away.
 *
 * `value` therefore holds only valid canonical tags and is what the parent saves; `rejected`
 * holds the red ones and is display-only.
 */
export function TagPicker({
  label,
  value,
  onChange,
  rejected,
  onRejectedChange,
  catalog,
  loadFailed = false,
  placeholder = "E.g Wework, Request, Workflow",
  required = false,
  inputId = "tag-picker",
}: {
  label: string;
  value: string[];
  onChange: (tags: string[]) => void;
  rejected: string[];
  onRejectedChange: (tags: string[]) => void;
  /** null until the catalogue has loaded. Nothing can be validated until it arrives. */
  catalog: AppCatalog | null;
  /** True when the catalogue fetch failed outright, as opposed to still being in flight — the
   * two look identical from `catalog === null` but need very different messages. */
  loadFailed?: boolean;
  placeholder?: string;
  required?: boolean;
  inputId?: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  /** App → the app type(s) it belongs to, shown as a subtitle in the dropdown. Several apps
   * (Wiki, Office, Booking…) are sold under both Base Platform and Base Info+, so this is a
   * list, not a single value. */
  const appTypeOf = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const { appType, apps } of catalog?.appTypes ?? []) {
      for (const app of apps) map.set(app, [...(map.get(app) ?? []), appType]);
    }
    return map;
  }, [catalog]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (catalog?.allApps ?? [])
      .filter((app) => !value.includes(app))
      .filter((app) => !q || app.toLowerCase().includes(q));
  }, [catalog, query, value]);

  // Close on click-away, so the dropdown behaves like every other one the CO Member uses.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  useEffect(() => setHighlight(0), [query]);

  function addCanonical(app: string) {
    if (!value.includes(app)) onChange([...value, app]);
    setQuery("");
  }

  /** Commits whatever has been typed, accepting several at once ("Wework, Request, Workflow").
   * Each entry is resolved case-insensitively; anything the catalogue doesn't know goes to
   * `rejected` rather than into the saved set. */
  function commitTyped() {
    // Nothing can be judged against a catalogue that hasn't arrived. Committing here would mark
    // every entry — `Wework` included — as "not a Base app", which is both wrong and alarming.
    // The button is disabled for this window too; this is the keyboard path's guard.
    if (!catalog) return;

    const entries = query
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (entries.length === 0) return;

    const accepted = [...value];
    const refused = [...rejected];
    for (const raw of entries) {
      const canonical = normalizeTag(raw, catalog.allApps);
      if (canonical) {
        if (!accepted.includes(canonical)) accepted.push(canonical);
      } else if (!refused.some((t) => t.toLowerCase() === raw.toLowerCase())) {
        refused.push(raw);
      }
    }
    onChange(accepted);
    onRejectedChange(refused);
    setQuery("");
    // Committing typed text ends that interaction, so get the panel out of the way; picking from
    // the list leaves it open, because that's the flow where you usually want several.
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      // Enter takes the highlighted suggestion when the dropdown is showing one, and otherwise
      // commits the raw text — so both "type three letters and hit Enter" and "type the full
      // name and hit Enter" do what the CO Member expects.
      const picked = open ? suggestions[highlight] : undefined;
      if (picked && query.trim()) addCanonical(picked);
      else commitTyped();
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "," ) {
      e.preventDefault();
      commitTyped();
    }
  }

  return (
    <div className="flex flex-col gap-1" ref={rootRef}>
      <label htmlFor={inputId} className="text-sm text-body">
        {label} {required && <span className="text-accent">*</span>}
      </label>

      <div className="flex flex-wrap gap-2">
        {value.map((tag) => (
          <span
            key={tag}
            className="flex items-center gap-1 rounded bg-surface-alt px-2 py-1 text-sm text-ink"
          >
            {tag}
            <button
              type="button"
              aria-label={`Remove ${tag}`}
              onClick={() => onChange(value.filter((t) => t !== tag))}
              className="text-accent"
            >
              ×
            </button>
          </span>
        ))}
        {/* The red rectangle the product owner asked for: the offending entry stays on screen,
            clearly marked, instead of vanishing silently or being saved by accident. */}
        {rejected.map((tag) => (
          <span
            key={tag}
            data-testid="rejected-tag"
            className="flex items-center gap-1 rounded border-2 border-accent bg-surface px-2 py-1 text-sm text-accent"
          >
            {tag}
            <button
              type="button"
              aria-label={`Remove ${tag}`}
              onClick={() => onRejectedChange(rejected.filter((t) => t !== tag))}
              className="text-accent"
            >
              ×
            </button>
          </span>
        ))}
      </div>

      <div className="relative flex gap-2">
        <input
          id={inputId}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${inputId}-listbox`}
          aria-autocomplete="list"
          placeholder={placeholder}
          className="flex-1 rounded border border-surface-alt px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={commitTyped}
          disabled={!catalog}
          className="rounded bg-surface-alt px-3 py-2 text-sm disabled:opacity-50"
        >
          Add
        </button>

        {open && (
          <ul
            id={`${inputId}-listbox`}
            role="listbox"
            className="absolute left-0 top-full z-20 mt-1 max-h-64 w-full max-w-md overflow-y-auto rounded border border-surface-alt bg-surface shadow-lg"
          >
            {suggestions.map((app, i) => (
              <li key={app}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === highlight}
                  // onMouseDown, not onClick: the input's blur would otherwise tear the dropdown
                  // down before the click ever lands.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addCanonical(app);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={`flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm ${
                    i === highlight ? "bg-surface-alt text-ink" : "text-body"
                  }`}
                >
                  <span>{app}</span>
                  <span className="shrink-0 text-xs text-body/70">
                    {(appTypeOf.get(app) ?? []).join(" · ")}
                  </span>
                </button>
              </li>
            ))}
            {suggestions.length === 0 && (
              <li className="px-3 py-2 text-sm text-body">
                {catalog
                  ? `No Base app matches "${query.trim()}".`
                  : loadFailed
                    ? "Couldn't load the Base app list. Reload the page to try again."
                    : "Loading the Base app list…"}
              </li>
            )}
          </ul>
        )}
      </div>

      {loadFailed && (
        <p role="alert" className="text-xs text-accent">
          Couldn't load the Base app list, so tags can't be checked right now. Reload the page to
          try again.
        </p>
      )}

      {rejected.length > 0 && (
        <p
          role="alert"
          className="rounded border border-accent bg-surface-alt px-3 py-2 text-xs text-accent"
        >
          Not a Base app: {rejected.join(", ")}. Pick from the list instead — these are outlined in
          red above and will not be saved.
        </p>
      )}
    </div>
  );
}
